import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Link } from 'react-router-dom'
import { 
  FiDroplet, 
  FiThermometer, 
  FiTool, 
  FiClock, 
  FiPhone,
  FiCheckCircle,
  FiArrowRight
} from 'react-icons/fi'

const PageContainer = styled.div`
  padding-top: 80px;
`

const HeroSection = styled.section`
  background: var(--gradient-hero);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 80px;
    background: var(--color-bg);
    clip-path: polygon(0 40%, 100% 0, 100% 100%, 0 100%);
  }
`

const HeroContent = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
`

const ServicesSection = styled.section`
  padding: 6rem 0;
  background: var(--color-bg);
`

const ServicesGrid = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
  display: grid;
  gap: 4rem;
`

const ServiceCard = styled(motion.div)`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 3rem;
  align-items: center;
  background: var(--gradient-card);
  padding: 3rem;
  border-radius: var(--border-radius-xl);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(59, 130, 246, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent);
    transition: left 0.6s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 60px rgba(59, 130, 246, 0.4);
    border-color: rgba(59, 130, 246, 0.4);

    &::before {
      left: 100%;
    }
  }

  &:nth-child(even) {
    grid-template-columns: 1fr 200px;
    
    .service-content {
      order: 1;
    }
    
    .service-icon {
      order: 2;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    
    &:nth-child(even) {
      .service-content {
        order: 1;
      }
      
      .service-icon {
        order: 2;
      }
    }
  }
`

const ServiceImage = styled.div`
  width: 200px;
  height: 200px;
  margin: 0 auto;
  border-radius: 25px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  position: relative;
  background: var(--gradient-accent);
  padding: 4px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 21px;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`

const ServiceContent = styled.div`
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: var(--color-text);
  }

  p {
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
    color: var(--color-text-light);
  }
`

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    color: var(--color-text);

    svg {
      color: var(--color-success);
      flex-shrink: 0;
    }
  }
`

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    
    .btn {
      width: 100%;
      max-width: 280px;
    }
  }
`

const CTASection = styled.section`
  background: var(--gradient-dark);
  color: white;
  padding: 5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 50%);
    animation: pulse 4s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.3; }
  }

  h2 {
    color: white;
  }

  p {
    color: rgba(255, 255, 255, 0.9);
  }
`

const ServicesPage: React.FC = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 })
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 })

  const services = [
    {
      image: '/images/81a57df0-32c8-4143-8628-83c07947047d.png',
      title: 'Instalacje hydrauliczne',
      description: 'Kompleksowy serwis instalacji wodno-kanalizacyjnych dla obiektów komercyjnych i przemysłowych.',
      features: [
        'Naprawy i wymiany rur',
        'Serwis armatury i baterii',
        'Instalacje sanitarne',
        'Systemy wodociągowe',
        'Kanalizacja przemysłowa'
      ]
    },
    {
      image: '/images/be580015-4c0d-4fa3-b2b6-3ba7a3441b5a.png',
      title: 'Instalacje grzewcze i C.O.',
      description: 'Profesjonalny serwis systemów grzewczych zapewniający komfort cieplny w Twoim obiekcie.',
      features: [
        'Naprawy kotłów gazowych',
        'Wymiana grzejników',
        'Serwis systemów C.O.',
        'Regulacja instalacji',
        'Przeglądy techniczne'
      ]
    },
    {
      image: '/images/4cb98481-bb7f-4e6f-9548-66f6f6004bdf.png',
      title: 'Ślusarka i drobne remonty',
      description: 'Usługi ślusarskie i remontowe dostosowane do potrzeb obiektów biznesowych.',
      features: [
        'Naprawy drzwi i okien',
        'Serwis bram przemysłowych',
        'Prace spawalnicze',
        'Remonty po zalaniach',
        'Montaż elementów metalowych'
      ]
    },
    {
      image: '/images/dd6c30f4-edbc-43bc-9dc6-45ca4f31729c.png',
      title: 'Zaplanowane serwisy',
      description: 'Regularne przeglądy i konserwacje w ustalonych terminach.',
      features: [
        'Przeglądy okresowe',
        'Konserwacje planowe',
        'Optymalizacja systemów',
        'Doradztwo techniczne',
        'Raporty z wykonanych prac'
      ]
    }
  ]

  return (
    <PageContainer>
      <HeroSection ref={heroRef}>
        <HeroContent>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            Nasze usługi
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ fontSize: '1.25rem', opacity: 0.9 }}
          >
            Kompleksowe rozwiązania techniczne dla Twojej firmy
          </motion.p>
        </HeroContent>
      </HeroSection>

      <ServicesSection ref={servicesRef}>
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={servicesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <ServiceImage className="service-icon">
                <img src={service.image} alt={service.title} />
              </ServiceImage>
              <ServiceContent className="service-content">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ServiceFeatures>
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>
                      <FiCheckCircle />
                      {feature}
                    </li>
                  ))}
                </ServiceFeatures>
                <Link to="/kontakt" className="btn btn-primary">
                  Zapytaj o wycenę
                  <FiArrowRight style={{ marginLeft: '0.5rem' }} />
                </Link>
              </ServiceContent>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </ServicesSection>

      <CTASection>
        <div className="container">
          <h2>Potrzebujesz pomocy technicznej?</h2>
          <p style={{ fontSize: '1.125rem', marginBottom: '2rem' }}>
            Skontaktuj się z nami i otrzymaj bezpłatną wycenę
          </p>
          <CTAButtons>
            <Link to="/kontakt" className="btn btn-primary btn-lg">
              Skontaktuj się z nami
            </Link>
            <a href="tel:510184702" className="btn btn-secondary btn-lg">
              <FiPhone />
              510 184 702
            </a>
          </CTAButtons>
        </div>
      </CTASection>
    </PageContainer>
  )
}

export default ServicesPage 